import { Image } from 'expo-image';
import { useRef, useState } from 'react';
import {
  Platform,
  ScrollView,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import { View, XStack, YStack } from 'tamagui';

import MaybeGrayscale from '@/components/ui/MaybeGrayscale';
import PSBadge, {
  type PSBadgeSize,
  type PSBadgeVariant,
} from '@/components/ui/PSBadge';
import PSIcon from '@/components/ui/PSIcon';

/**
 * ImageCarousel — a paging image carousel (molecule).
 *
 * Swipe on device; clickable arrows + tappable dots on web (so it's usable in
 * both surfaces). Images are a fixed 3:4 portrait ratio (height = width × 4/3) —
 * PS event imagery is always 3:4, so ratio is baked in rather than a prop.
 *
 * The horizontal pager is the one spot we use RN's ScrollView directly: we need
 * its imperative `scrollTo` for the web arrows and Tamagui has no paging pager.
 * Everything visual around it is Tamagui.
 *
 * `badgeLabel` is an optional status label floated over the image's bottom-left (a
 * `PSBadge`) — the event page passes "Sold out" / "Missed out" so an
 * unbookable event still signals WHY the ticket UI is gone. Render-only: the
 * caller decides the text, `badgeVariant` and `badgeSize`; the carousel just places it. No
 * `badgeLabel` ⇒ no overlay.
 *
 * `grayScale` sends every slide monotone (matches the `EventCard` treatment).
 */

// Portrait 3:4 (width:height) → height is 4/3 of the measured width.
const IMAGE_RATIO = 4 / 3;
// Web arrow button diameter (used to vertically center it over the image).
const ARROW = 40;

export default function ImageCarousel({
  images,
  badgeLabel,
  badgeVariant = 'neutral',
  badgeSize = 'default',
  grayScale = false,
}: {
  images: string[];
  /** Optional status label floated at the image's bottom-left, e.g. "Sold out". */
  badgeLabel?: string;
  badgeVariant?: PSBadgeVariant;
  badgeSize?: PSBadgeSize;
  grayScale?: boolean;
}) {
  const scrollRef = useRef<ScrollView>(null);
  const [width, setWidth] = useState(0);
  const [index, setIndex] = useState(0);
  const count = images.length;

  const onLayout = (e: LayoutChangeEvent) =>
    setWidth(e.nativeEvent.layout.width);

  // Track the index on every scroll frame (not just momentum end) so the dots
  // follow a swipe / trackpad drag on both web and native.
  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (width > 0) {
      const i = Math.round(e.nativeEvent.contentOffset.x / width);
      setIndex((prev) =>
        prev === i ? prev : Math.max(0, Math.min(count - 1, i))
      );
    }
  };

  // Wraps around: going left from the first image lands on the last, and right
  // from the last lands on the first ( ((i % count) + count) % count handles
  // the negative case ).
  const goTo = (i: number) => {
    const next = ((i % count) + count) % count;
    // setIndex(next);
    scrollRef.current?.scrollTo({ x: next * width, animated: true });
  };

  const height = width * IMAGE_RATIO;

  return (
    <YStack onLayout={onLayout}>
      {width > 0 ? (
        <>
          <View
            position="relative"
            width={width}
            height={height}
            rounded="$base"
            overflow="hidden"
          >
            <ScrollView
              ref={scrollRef}
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              onScroll={onScroll}
              scrollEventThrottle={16}
              style={{ width, height }}
            >
              {images.map((uri, i) => (
                <MaybeGrayscale key={`${uri}-${i}`} enabled={grayScale}>
                  <Image
                    source={{ uri }}
                    style={{ width, height }}
                    contentFit="cover"
                    transition={200}
                  />
                </MaybeGrayscale>
              ))}
            </ScrollView>

            {/* Prev / next arrows — web only (device relies on swipe). They wrap
                around at the ends rather than disabling. */}
            {Platform.OS === 'web' && count > 1 ? (
              <>
                <Arrow
                  side="left"
                  top={height / 2 - ARROW / 2}
                  onPress={() => goTo(index - 1)}
                />
                <Arrow
                  side="right"
                  top={height / 2 - ARROW / 2}
                  onPress={() => goTo(index + 1)}
                />
              </>
            ) : null}

            {/* Status badge over the image's bottom-left (sold-out / past). */}
            {badgeLabel ? (
              <View position="absolute" b="$md" l="$md">
                <PSBadge
                  label={badgeLabel}
                  variant={badgeVariant}
                  size={badgeSize}
                />
              </View>
            ) : null}
          </View>

          {count > 1 ? (
            <XStack marginTop="$md" justify="center" gap="$sm">
              {images.map((_, i) => (
                // Plain View, not Tamagui's Circle: the active dot is a 20×8
                // pill, and Circle's keep-width==height sizing recalculates on
                // the width change, which jitters on web. `rounded="$full"`
                // gives the pill/round shape from radius alone.
                <View
                  key={i}
                  onPress={() => goTo(i)}
                  cursor="pointer"
                  height={8}
                  width={i === index ? 20 : 8}
                  rounded="$full"
                  backgroundColor={i === index ? '$color' : '$borderColor'}
                />
              ))}
            </XStack>
          ) : null}
        </>
      ) : (
        <View height={1} />
      )}
    </YStack>
  );
}

function Arrow({
  side,
  top,
  onPress,
}: {
  side: 'left' | 'right';
  top: number;
  onPress: () => void;
}) {
  return (
    <View
      position="absolute"
      top={top}
      l={side === 'left' ? '$md' : undefined}
      r={side === 'right' ? '$md' : undefined}
      cursor="pointer"
      onPress={onPress}
      height={ARROW}
      width={ARROW}
      rounded="$full"
      items="center"
      justify="center"
      backgroundColor="$background"
      borderWidth="$borderWidth.hairline"
      borderColor="$borderColor"
    >
      <PSIcon
        name={side === 'left' ? 'chevron-back' : 'chevron-forward'}
        size={20}
      />
    </View>
  );
}
